import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import ctl from '@netlify/classnames-template-literals'
import { ReactNode } from 'react'
import { FilterBox, FilterBoxTheme } from './filter-box'
import { CheckmarkIcon, ChevronIcon } from './filter-icons'

export type SelectFilterOption<T extends string> = {
	id: T
	label: ReactNode
	separator?: boolean
}

export type SelectFilterProps<T extends string> = {
	options: ReadonlyArray<SelectFilterOption<T>>
	value: T
	onChange: (value: T) => void
	placeholder?: string
	theme?: FilterBoxTheme
}

export function SelectFilter<T extends string>({
	options,
	value,
	onChange,
	placeholder,
	theme,
}: SelectFilterProps<T>) {
	const selectedOption = options.find((option) => option.id === value && !option.separator)

	return (
		<Listbox value={value} onChange={onChange}>
			<FilterBox theme={theme}>
				<ListboxButton
					className={ctl(`
						rounded-md p-2 pr-8 pl-3 w-full text-left
						focus:outline-none
						group relative
						text-sm
					`)}
				>
					{selectedOption ? selectedOption.label : placeholder}
					<div className="absolute right-3 top-1/2 -translate-y-1/2">
						<ChevronIcon />
					</div>
				</ListboxButton>
			</FilterBox>
			<ListboxOptions
				anchor="bottom"
				className="bg-white min-w-[var(--button-width)] [--anchor-gap:8px] ring-1 py-1 ring-grey-100 shadow-md rounded text-sm text-grey-600"
			>
				{options.map((option) => {
					if (option.separator) {
						return <div key={option.id} className="my-1 mx-2 border-t border-grey-200" />
					}

					return (
						<ListboxOption
							key={option.id}
							value={option.id}
							className={ctl(`
								relative
								group
								bg-white
								text-grey-800
								data-[selected]:bg-blue-50
								data-[selected]:text-blue-600
								cursor-pointer data-focus:bg-blue-100 mx-1 px-2 py-1 rounded
							`)}
						>
							{option.label}
							<div className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-500 opacity-0 group-data-[selected]:opacity-100">
								<CheckmarkIcon />
							</div>
						</ListboxOption>
					)
				})}
			</ListboxOptions>
		</Listbox>
	)
}
